import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiJson, socket } from '../lib/api';
import logo from '../assets/chitconnect-logo-blue.png';

const toInt = (v) => {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

export default function GroupChat() {
  const { groupId } = useParams();
  const gid = toInt(groupId);

  const me = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);
  const meId = toInt(me?.id);

  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  const bottomRef = useRef(null);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const mergeById = (prev, incoming) => {
    const map = new Map(prev.map((m) => [m.id, m]));
    for (const m of incoming) map.set(m.id, m);
    return Array.from(map.values()).sort((a, b) => a.id - b.id);
  };

  async function loadPage(beforeId = null, append = false) {
    const qs = new URLSearchParams();
    if (beforeId) qs.set('beforeId', String(beforeId));
    qs.set('limit', String(30));

    const r = await apiJson(`/api/messages/group/${gid}?${qs.toString()}`);
    if (!r.ok || !r.data?.ok) {
      throw new Error(r.data?.error || r.text || 'Failed to load messages');
    }

    const payload = r.data;
    setGroup(payload.group);
    setNextBeforeId(payload.next_before_id || null);
    setMessages((prev) => (append ? mergeById(payload.messages || [], prev) : (payload.messages || [])));

    const lastId = payload.messages?.length ? payload.messages[payload.messages.length - 1].id : 0;
    if (lastId) {
      apiJson(`/api/messages/group/${gid}/delivered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ last_delivered_message_id: lastId }),
      }).catch(() => {});

      apiJson(`/api/messages/group/${gid}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ last_read_message_id: lastId }),
      }).catch(() => {});
    }
  }

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError('');
      try {
        await loadPage(null, false);
        if (mounted) setTimeout(scrollToBottom, 50);
      } catch (e) {
        if (mounted) setError(e?.message || 'Failed to load chat');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    // Join secure group chat room for realtime delivery
    if (gid) socket.emit('joinGroupChat', { groupId: gid });

    return () => {
      mounted = false;
      if (gid) socket.emit('leaveGroupChat', { groupId: gid });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gid]);

  useEffect(() => {
    const handler = (payload) => {
      if (!payload || toInt(payload.group_id) !== gid) return;
      const msg = payload.message;
      if (!msg?.id) return;
      setMessages((prev) => mergeById(prev, [msg]));
      setTimeout(scrollToBottom, 30);
    };

    socket.on('group:newMessage', handler);
    return () => socket.off('group:newMessage', handler);
  }, [gid]);

  const loadOlder = async () => {
    if (!nextBeforeId) return;
    setLoadingMore(true);
    try {
      await loadPage(nextBeforeId, true);
    } catch (e) {
      setError(e?.message || 'Failed to load earlier messages');
    } finally {
      setLoadingMore(false);
    }
  };

  const send = async () => {
    const body = String(text || '').trim();
    if (!body || sending) return;

    setSending(true);
    setError('');
    try {
      const r = await apiJson(`/api/messages/group/${gid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });

      if (!r.ok || !r.data?.ok) throw new Error(r.data?.error || r.text || 'Send failed');

      const msg = r.data.message;
      if (msg?.id) {
        setMessages((prev) => mergeById(prev, [msg]));
      }
      setText('');
      setTimeout(scrollToBottom, 30);
    } catch (e) {
      setError(e?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="main-content chat-page">
      <img src={logo} alt="ChitConnect Logo" className="group-logo" />

      <div className="chat-card">
        <div className="chat-header">
          <div>
            <Link to="/messages" className="chat-back">← Messages</Link>
            <h2 className="chat-title">{group?.name || 'Group chat'}</h2>
            <div className="chat-subtitle">Group #{gid}</div>
          </div>
        </div>

        {error && <p className="error-text" style={{ marginBottom: 8 }}>{error}</p>}

        {loading ? (
          <p>Loading…</p>
        ) : (
          <>
            {nextBeforeId && (
              <button
                className="chat-loadolder"
                onClick={loadOlder}
                disabled={loadingMore}
                type="button"
              >
                {loadingMore ? 'Loading…' : 'Load older'}
              </button>
            )}

            <div className="chat-window">
              {messages.length === 0 ? (
                <div className="chat-empty">No messages yet.</div>
              ) : (
                <>
                  {messages.map((m) => {
                    const mine = toInt(m.sender_id) === meId;
                    return (
                      <div key={m.id} className={`chat-row ${mine ? 'mine' : 'theirs'}`}>
                        <div className={`chat-bubble ${mine ? 'mine' : 'theirs'}`}>
                          {!mine && (
                            <div className="chat-sender">{m.sender_name || `User #${m.sender_id}`}</div>
                          )}
                          <div className="chat-body">{m.body}</div>
                          <div className="chat-time">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </>
              )}
            </div>

            <div className="chat-note">
              Only active members can access this chat. Emails/phone numbers are not shown.
            </div>

            <div className="chat-composer">
              <textarea
                className="chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message…"
                rows={2}
              />
              <button className="chat-send" onClick={send} disabled={sending} type="button">
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
